import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble
 * @property {HTMLElement} cartBubbleText
 * @property {HTMLElement} cartBubbleCount
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {MutationObserver | null} */
  bagCountObserver = null;

  /** @type {MutationObserver | null} */
  cartIconObserver = null;

  get currentCartCount() {
    const raw = this.refs?.cartBubbleCount?.textContent?.trim() ?? '0';
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  set currentCartCount(value) {
    const next = Number.isFinite(value) ? value : 0;
    if (this.refs?.cartBubbleCount) {
      this.refs.cartBubbleCount.textContent = next < 100 ? String(next) : '99+';
    }
  }

  updateBagLabel = (count) => {
    const bagEl = this.querySelector('.header-bag-text');
    if (!bagEl) return;

    const nextText =
      !Number.isFinite(count) || count <= 0
        ? 'BAG'
        : `BAG (${count < 100 ? String(count) : '99+'})`;

    if (bagEl.textContent === nextText) return;
    bagEl.textContent = nextText;
  };

  startBagCountObserver = () => {
    if (!this.refs?.cartBubbleCount) return;

    this.bagCountObserver?.disconnect();
    this.bagCountObserver = new MutationObserver(() => {
      this.updateBagLabel(this.currentCartCount);
    });

    this.bagCountObserver.observe(this.refs.cartBubbleCount, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  };

  startCartIconObserver = () => {
    this.cartIconObserver?.disconnect();
    this.cartIconObserver = new MutationObserver(() => {
      this.updateBagLabel(this.currentCartCount);
    });

    this.cartIconObserver.observe(this, {
      childList: true,
      subtree: true,
    });
  };

  /**
   * Hard source of truth: Shopify cart endpoint.
   * This makes BAG (n) survive re-renders + event weirdness.
   */
  syncFromCartJS = async () => {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return;
      const cart = await res.json();
      const count = Number(cart?.item_count ?? 0);

      // Update bubble + label in one place
      this.renderCartBubble(count, false, false);
      this.updateBagLabel(count);
    } catch (_) {
      // no-op
    }
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);

    // First sync (covers initial load + header re-renders)
    this.syncFromCartJS();

    this.startBagCountObserver();
    this.startCartIconObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);

    this.bagCountObserver?.disconnect();
    this.cartIconObserver?.disconnect();

    this.bagCountObserver = null;
    this.cartIconObserver = null;
  }

  onPageShow = (event) => {
    if (event.persisted) {
      this.syncFromCartJS();
    }
  };

  onCartUpdate = async (event) => {
    const itemCount = event?.detail?.data?.itemCount;

    // If the theme provided a number, use it.
    if (typeof itemCount === 'number') {
      this.renderCartBubble(itemCount, false, true);
      this.updateBagLabel(itemCount);
      return;
    }

    // Otherwise fall back to Shopify source of truth.
    await this.syncFromCartJS();
  };

  renderCartBubble = async (itemCount, _comingFromProductForm, animate = true) => {
    const count = Number.isFinite(itemCount) ? itemCount : 0;

    this.currentCartCount = count;

    this.refs.cartBubbleCount.classList.toggle('hidden', count === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', count === 0);

    this.classList.toggle('header-actions__cart-icon--has-cart', count > 0);

    // Store for cache restores
    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(count),
        timestamp: Date.now(),
      })
    );

    if (!animate || count === 0) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);
    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
